import Cookies from 'js-cookie';

export type LocaleShort = 'en' | 'fr' | 'en-GB';
const LOCALE_SHORT_TYPES: LocaleShort[] = ['en', 'fr', 'en-GB'];

/**
 * 'narrowSymbol' renders the bare currency symbol regardless of locale (USD is "$" even under en-CA, instead of "US$").
 * Older browsers may throw so we fall back to 'symbol'.
 */
export const currencyDisplay: 'symbol' | 'narrowSymbol' = (() => {
	try {
		new Intl.NumberFormat('en', { style: 'currency', currency: 'USD', currencyDisplay: 'narrowSymbol' });
		return 'narrowSymbol';
	}
	catch (_error) {
		return 'symbol';
	}
})();

/**
 * Get the locale the visitor explicitly chose with the locale switcher, if any
 *
 * @return {string|undefined}
 */
export function getSavedLocale(): string|undefined {
	return Cookies.get('locale') || localStorage.getItem('locale') || undefined;
}

/**
 * Get preferred browser locale but before we verify if the locale is set in the cookies, meaning
 * the user at one point chose a language with the locale switcher, so we will be using that one.
 *
 * @return {string|undefined}
 */
export default function getBrowserLocale(): string|undefined {
	const savedLocale: string|undefined = getSavedLocale();
	let locale: Locale|undefined;
	if(savedLocale) {
		locale = findSupportedLocale(savedLocale);
	}
	else {
		const navigatorLocale = navigator.languages !== undefined ? navigator.languages[0] : navigator.language;
		if (!navigatorLocale) {
			return undefined;
		}
		locale = findSupportedLocale(navigatorLocale);
	}
	return locale && locale.code;
}

/**
 * Get current supported locales
 *
 * @return {Locale[]}
 */
export function getSupportedLocales(): Locale[] {
	return [
		{
			code: 'en-CA',
			trimmed_code: 'en',
			name: 'English CA'
		},
		{
			code: 'fr-CA',
			trimmed_code: 'fr',
			name: 'Canadian French'
		},
		{
			code: 'en-GB',
			trimmed_code: 'en',
			name: 'English (UK)'
		}
	];
}

/**
 * Resolve a raw locale value to a canonical supported locale code.
 *
 * * This helper has a copy in Remy and Gusteau, any change of logic here must be replicated there.
 *
 * Precedence:
 *   1. Exact tag match (case-insensitive) against the candidate set — the restaurant's
 *      supported locales when provided, otherwise the global whitelist.
 *   2. Language-prefix match ('en-CA' → 'en', 'fr-CA' → 'fr'). A bare language code is
 *      preferred over a regional one, so 'en-US' resolves to 'en', never 'en-GB'.
 *   3. The restaurant's default locale, when restaurant locales are provided — a missing
 *      or unsupported language should land on the location's own language, not English.
 *   4. Fallback to 'en'.
 *
 * The returned value is always the canonical cased code (e.g. 'en-GB'). For all 'en'/'fr'
 * family inputs this yields the same result the previous 2-letter trimming did.
 *
 * @param {string | undefined} lang - raw locale value (e.g. i18n.locale)
 * @param {RestaurantLocale[]} [restaurantLocales] - restaurant's supported locales
 * @return {LocaleShort}
 */
export function resolveLocale(lang: string | undefined, restaurantLocales?: RestaurantLocale[]): LocaleShort {
	const supported: string[] = restaurantLocales?.length
		? restaurantLocales.map((restaurantLocale: RestaurantLocale) => restaurantLocale.locale_short)
		: LOCALE_SHORT_TYPES;

	// Missing or unsupported language both land on the restaurant's default locale when we know it.
	const defaultLocale: LocaleShort = (restaurantLocales?.find((restaurantLocale: RestaurantLocale) => restaurantLocale.is_default)?.locale_short as LocaleShort) ?? 'en';

	// Take the first tag from a possible locale list ('en-GB,en;q=0.9' → 'en-GB').
	const tag = (lang ?? '').split(',')[0].split(';')[0].trim().replace('_', '-');
	if (!tag) {
		return defaultLocale;
	}

	// Exact tag match, returning the canonical cased code from the candidate set.
	const exact = supported.find((code: string) => code.toLowerCase() === tag.toLowerCase());
	if (exact) {
		return exact as LocaleShort;
	}

	// Language-prefix match: 'en-CA' → 'en', 'fr-CA' → 'fr'.
	const prefix = tag.split('-')[0].toLowerCase();
	const bare = supported.find((code: string) => code.toLowerCase() === prefix);
	if (bare) {
		return bare as LocaleShort;
	}

	return defaultLocale;
}

/**
 * Localize a field value based on the provided locale and element's localization data.
 *
 * * This helper has a copy in Remy and Gusteau, any change of logic here must be replicated there.
 *
 * @param {LocaleElement} element - Element containing a localization property
 * @param {string} field - The field to localize inside the element
 * @param {string} [locale] - Locale string (e.g. 'en-CA', 'fr-CA')
 * @param {RestaurantLocale[]} [restaurantLocales]
 * @return {string}
 */
export function localize(element: LocaleElement, field: string, locale?: string, restaurantLocales?: RestaurantLocale[]): string {
	if (!locale || !restaurantLocales?.length || !element?.localization) {
		return element[field] || '';
	}

	// Resolve to a canonical supported code, then walk the content fallback chain:
	// exact locale entry → language-prefix entry (e.g. 'en-GB' → 'en') → base field.
	const resolved = resolveLocale(locale, restaurantLocales);
	const candidates = resolved.includes('-') ? [resolved, resolved.split('-')[0]] : [resolved];

	for (const code of candidates) {
		if (element.localization[code]?.[field]) {
			return element.localization[code][field];
		}
	}
	return element[field] || '';
}

/**
 * Resolve the full supported locale code the menu should use after loading the restaurant.
 *
 * Priority:
 *   1. A locale the visitor explicitly chose (cookie/localStorage).
 *   2. The browser's locale, when the restaurant supports its language.
 *   3. The restaurant's default locale.
 *
 * Example: a first-time guest with an en-CA browser visiting an en-GB-only restaurant gets 'en-GB'.
 *
 * @param {string} currentLocale - the active i18n locale (e.g. 'en-CA')
 * @param {RestaurantLocale[]} [restaurantLocales]
 * @return {string}
 */
export function getRestaurantInitialLocale(currentLocale: string, restaurantLocales?: RestaurantLocale[]): string {
	const savedLocale: string|undefined = getSavedLocale();
	if (savedLocale || !restaurantLocales?.length) {
		return currentLocale;
	}

	// Map the resolved short code back to a full supported code ('en-GB' → 'en-GB', 'en' → 'en-CA', 'fr' → 'fr-CA')
	const resolved: LocaleShort = resolveLocale(currentLocale, restaurantLocales);
	const supportedLocales: Locale[] = getSupportedLocales();
	const match: Locale|undefined = supportedLocales.find((locale: Locale) => locale.code.toLowerCase() === resolved.toLowerCase())
		?? supportedLocales.find((locale: Locale) => locale.trimmed_code === resolved);
	return match ? match.code : currentLocale;
}

/**
 * We check if the full locale passed (IE: fr-CA) is supported, if not, we trim it down to its
 * language prefix (IE: fr) and we will fetch the closest file to their language. If both are
 * undefined we will fallback to the default fallback.
 *
 * @param {string} localeCountryCode
 * @return {Locale|undefined}
 */
export function findSupportedLocale(localeCountryCode: string): Locale|undefined {
	const trimmedLocaleCode: string = localeCountryCode.trim().split(/-|_/)[0];
	const supportedLocales: Locale[] = getSupportedLocales();
	const localeWithCountryCode: Locale|undefined = supportedLocales.find((locale: Locale) => {
		if(locale.code === localeCountryCode) {
			return locale;
		}
	});
	const trimmedLocale: Locale|undefined = supportedLocales.find((locale: Locale) => {
		if(locale.trimmed_code === trimmedLocaleCode) {
			return locale;
		}
	});
	return localeWithCountryCode ? localeWithCountryCode : trimmedLocale;
}